import * as moment from 'moment';
import * as registrationConsts from '@/constants/Registration';
import CouponService from '@/services/coupons/CouponService';
import RegistrationService from '@/services/users/RegistrationService';
import TaxService from '@/services/users/TaxService';
import i18n from '@/i18n';

const getDefaultState = () => {
    return {
        sections: {
            [registrationConsts.SECTION_ACCOUNT]: false,
            [registrationConsts.SECTION_DELIVERY]: false,
            [registrationConsts.SECTION_PAYMENT]: false,
        },
        details: {
            incompleteRegistrantUuid: null,
            welcomeName: null,
            account: {
                firstName: null,
                lastName: null,
                email: null,
                password: null,
                optInToMarketing: process.env.VUE_APP_COUNTRY_ISO === 'USA',
                optInToTextCalls: true,
                optInToEmailsLetters: true,
                optInToThirdPartyMarketing: false,
                doubleOptInMarketing: false,
                doubleOptInSpoilerMarketing: false,
                telephone: null,
            },
            billingAddress: {
                addressLine1: null,
                addressLine2: null,
                city: null,
                country: process.env.VUE_APP_COUNTRY_ISO,
                postcode: null,
                state: null,
            },
            deliveryAddress: {
                addressLine1: null,
                addressLine2: null,
                city: null,
                country: process.env.VUE_APP_COUNTRY_ISO,
                postcode: null,
                state: null,
            },
            couponCode: null,
            friendCode: null,
            couponProperties: null,
            deliveryAddressIsBilling: true,
            deliveryComment: null,
            propertyType: null,
            card: {},
            firstPossibleDeliveryDate: null,
            firstDeliveryDateDesired: null,
            subscriptionsHostedPage: null,
            subscriptionsHostedPageUrl: null,
            courierName: null,
            couponDetails: null,
        },
        plans: [
            {
                plan_name: i18n.t('PLAN_NAME_WEEKLY'),
                plan_interval: 'weekly',
            },
            {
                plan_name: i18n.t('PLAN_NAME_FORTNIGHTLY'),
                plan_interval: 'fortnightly',
            },
        ],
        taxes: {},
        selectedPlan: null,
        checkoutPrice: 0,
    };
};

export default {
    namespaced: true,
    state: getDefaultState(),
    mutations: {
        resetState(state) {
            Object.assign(state, getDefaultState());
        },
        setWelcomeName: (state, name) => state.details.welcomeName = name,
        setFirstName: (state, name) => state.details.account.firstName = name,
        setLastName: (state, name) => state.details.account.lastName = name,
        setEmail: (state, email) => state.details.account.email = email,
        setPassword: (state, password) => state.details.account.password = password,
        setOptInMarketing: (state, value) => state.details.account.optInToMarketing = value,
        setOptInEmailsLetters: (state, value) => state.details.account.optInToEmailsLetters = value,
        setOptInTextCalls: (state, value) => state.details.account.optInToTextCalls = value,
        setOptInThirdPartyMarketing: (state, value) => state.details.account.optInToThirdPartyMarketing = value,
        setDoubleOptInMarketing: (state, value) => state.details.account.doubleOptInMarketing = value,
        setDoubleOptInSpoilerMarketing: (state, value) => state.details.account.doubleOptInSpoilerMarketing = value,
        setSectionComplete: (state, section) => state.sections[section] = true,
        setDeliveryAddress: (state, address) => {
            state.details.deliveryAddress = Object.assign(state.details.deliveryAddress, address);
        },
        setBillingAddress: (state, address) => {
            state.details.billingAddress = Object.assign(state.details.billingAddress, address);
        },
        setFirstPossibleDeliveryDate: (state, date) => state.details.firstPossibleDeliveryDate = date,
        setCourierName: (state, name) => state.details.courierName = name,
        setFirstDeliveryDateDesired: (state, date) => state.details.firstDeliveryDateDesired = date,
        setDeliveryComment: (state, comment) => state.details.deliveryComment = comment,
        setPropertyType: (state, type) => state.details.propertyType = type,
        setTelephone: (state, telephone) => state.details.account.telephone = telephone,
        setDeliveryAddressIsBilling: (state, bool) => state.details.deliveryAddressIsBilling = bool,
        setCouponCode: (state, code) => state.details.couponCode = code,
        setFriendCode: (state, code) => state.details.friendCode = code,
        setCouponProperties: (state, code) => state.details.couponProperties = code,
        setIncompleteRegistrantUuid: (state, uuid) => state.details.incompleteRegistrantUuid = uuid,
        setSubscriptionsHostedPage: (state, hostedPage) => state.details.subscriptionsHostedPage = hostedPage,
        setSubscriptionsHostedPageUrl: (state, url) => state.details.subscriptionsHostedPageUrl = url,
        setPaymentToken: (state, tokenId) => state.details.paymentToken = tokenId,
        setPaymentType: (state, type) => state.details.paymentType = type,
        setTaxes: (state, payload) => state.taxes = payload,
        setCouponDetails: (state, payload) => state.details.couponDetails = payload,
        setSelectedPlan: (state, value) => state.selectedPlan = value,
        setCheckoutPrice: (state, value) => state.checkoutPrice = value,
    },
    actions: {
        getCouponByCouponCode({ commit }, couponCode) {
            return new Promise((resolve, reject) => {
                CouponService.getCouponByCouponCode(couponCode)
                    .then(response => {
                        commit('setCouponDetails', response.data.properties);
                        resolve(response);
                    })
                    .catch(error => {
                        reject(error);
                    });
            });
        },

        fetchTaxes({ commit }, payload) {
            return new Promise((resolve, reject) => {
                TaxService.getTaxes(payload)
                    .then((res) => {
                        commit('setTaxes', res.data);
                        resolve(res);
                    })
                    .catch(error => reject(error));
            });
        },

        registerCustomer({ commit }, paymentDetails) {
            return new Promise((resolve, reject) => {
                RegistrationService.registerCustomer(paymentDetails)
                    .then((res) => {
                        commit('setSectionComplete', registrationConsts.SECTION_PAYMENT);
                        resolve(res);
                    })
                    .catch(error => reject(error));
            });
        },

        registerCanvassedCustomer({ commit }, paymentDetails) {
            return new Promise((resolve, reject) => {
                RegistrationService.registerCanvassedCustomer(paymentDetails)
                    .then((res) => {
                        commit('setSectionComplete', registrationConsts.SECTION_PAYMENT);
                        resolve(res);
                    })
                    .catch(error => reject(error));
            });
        },
    },
    getters: {
        getWelcomeName: state => state.details.welcomeName,
        getFirstName: state => state.details.account.firstName,
        getLastName: state => state.details.account.lastName,
        getFullName: state => {
            return `${state.details.account.firstName} ${state.details.account.lastName}`;
        },
        getEmail: state => state.details.account.email,
        getPassword: state => state.details.account.password,
        getOptInMarketing: state => state.details.account.optInToMarketing,
        getOptInTextCalls: state => state.details.account.optInToTextCalls,
        getOptInEmailsLetters: state => state.details.account.optInToEmailsLetters,
        getOptInThirdPartyMarketing: state => state.details.account.optInToThirdPartyMarketing,
        getDoubleOptInMarketing: state => state.details.account.doubleOptInMarketing,
        getDoubleOptInSpoilerMarketing: state => state.details.account.doubleOptInSpoilerMarketing,
        getDeliveryAddress: state => state.details.deliveryAddress,
        getBillingAddress: state => state.details.billingAddress,
        getFirstPossibleDeliveryDate: state => state.details.firstPossibleDeliveryDate,
        getCourierName: state => state.details.courierName,
        getFirstDeliveryDay: state => {
            return state.details.firstPossibleDeliveryDate ? moment(state.details.firstPossibleDeliveryDate).format('dddd') : null;
        },
        getFirstDeliveryDateDesired: state => state.details.firstDeliveryDateDesired,
        getDeliveryComment: state => state.details.deliveryComment,
        getPropertyType: state => state.details.propertyType,
        getTelephone: state => state.details.account.telephone,
        getDeliveryAddressIsBilling: state => state.details.deliveryAddressIsBilling,
        getCouponCode: state => state.details.couponCode,
        getFriendCode: state => state.details.friendCode,
        getCouponProperties: state => state.details.couponProperties,
        getIncompleteRegistrantUuid: state => state.details.incompleteRegistrantUuid,
        getSectionCompleted: state => section => state.sections[section],
        getSubscriptionsHostedPage: state => state.details.subscriptionsHostedPage,
        getSubscriptionsHostedPageUrl: state => state.details.subscriptionsHostedPageUrl,
        getPaymentToken: state => state.details.paymentToken,
        getPaymentType: state => state.details.paymentType,
        getTaxes: state => state.taxes,
        getCouponDetails: state => state.details.couponDetails,
        getAllPlans: state => state.plans,
        getSelectedPlan: state => state.selectedPlan || state.plans[0],
        getCheckoutPrice: state => state.checkoutPrice,
    },
};
